import { ContainerJourneyResponseDto } from '@planning/app/api'
import { EventTypes } from '@planning/messages/eventsTypes'
import { GetContainerJourneysQuery } from '@planning/messages/queries/getContainerJourneysQueryHandler'
import { ItemStore } from '@planning/rt-stores/base/ItemStore'
import { ContainerItemStore } from '@planning/rt-stores/container/ContainerItemStore'
import { HoldItemStore } from '@planning/rt-stores/hold/HoldItemStore'
import { OrderItemStore } from '@planning/rt-stores/order/OrderItemStore'
import { IEntityStore } from '@planning/rt-stores/types'
import containerJourneysService from '@planning/services/containerJourneys'
import { IEvent, IMessageBus } from '@tom-ui/utils'
import _ from 'lodash'
import { action, computed, makeObservable, runInAction } from 'mobx'
import { ContainerJourneyItem, IContainerJourneyItem } from './ContainerJourneyItem'

export class ContainerJourneyItemStore
  extends ItemStore<ContainerJourneyResponseDto, IContainerJourneyItem>
  implements IEntityStore<IContainerJourneyItem>
{
  private loadedContainerIds: Set<number> = new Set()
  private orderItemStore?: OrderItemStore
  private holdItemStore?: HoldItemStore
  private fetchedAll = false

  constructor(
    private readonly messageBus: IMessageBus,
    containerItemStore: ContainerItemStore,
    holdItemStore?: HoldItemStore,
  ) {
    super(
      (key, data) =>
        new ContainerJourneyItem(
          this,
          key,
          data,
          this.orderItemStore,
          containerItemStore,
          this.holdItemStore,
        ),
    )
    this.holdItemStore = holdItemStore
    makeObservable(this, {
      journeysByInboundOrderId: computed,
      journeysByOutboundOrderId: computed,
      journeysByContainerId: computed,
      receiveContainerJourneysMessage: action,
      receiveContainerJourneysUpsertedEventMessage: action,
    })

    messageBus.subscribeEvent(GetContainerJourneysQuery.type, this.receiveContainerJourneysMessage)
    messageBus.subscribeEvent(
      EventTypes.ContainerJourneysUpsertedEvent,
      this.receiveContainerJourneysUpsertedEventMessage,
    )
  }

  connect = (orderItemStore: OrderItemStore) => {
    this.orderItemStore = orderItemStore
  }
  connectHoldItemStore = (holdItemStore: HoldItemStore) => {
    this.holdItemStore = holdItemStore
  }

  connectHoldStore = (holdItemStore: HoldItemStore) => {
    this.holdItemStore = holdItemStore
  }

  get journeysByInboundOrderId() {
    return _(this.elements)
      .mapKeys(value => value.data.inboundOrderId)
      .value()
  }

  get journeysByOutboundOrderId() {
    return _(this.elements)
      .mapKeys(value => value.data.outboundOrderId)
      .value()
  }

  get journeysByContainerId() {
    return _(this.elements)
      .mapKeys(value => value.data.containerId)
      .value()
  }

  get items() {
    return _.values(this.elements)
  }

  fetch = async (
    containerIds: number[] = [],
    includeCompleted = false,
    journeyIds: number[] = [],
  ) => {
    const containerIdsToLoad = containerIds.filter(id => !this.loadedContainerIds.has(id))
    const journeyIdsToBeLoaded = journeyIds.filter(id => !_.has(this.elements, id))

    if (!journeyIdsToBeLoaded.length && !containerIdsToLoad.length) return

    containerIdsToLoad.forEach(id => this.loadedContainerIds.add(id))

    await this.messageBus.dispatchQuery(
      new GetContainerJourneysQuery(containerIdsToLoad, includeCompleted, journeyIdsToBeLoaded),
    )
  }

  fetchByContainerNumbers = async (containerNumbers: string[]) => {
    const journeys = await containerJourneysService.getByContainerNumbers(containerNumbers)
    runInAction(() => {
      this.upsertBulk(journeys)
    })
  }

  fetchAll = async (includeCompleted?: boolean) => {
    if (this.fetchedAll) return

    // Fetch all journeys (empty array means all)
    await this.messageBus.dispatchQuery(new GetContainerJourneysQuery([], includeCompleted, []))
    // As we don't know all containerIds to be loaded, so loadedContainerIds will be set in receiveContainerJourneysMessage

    this.fetchedAll = true
  }

  fetchByOrderId = async (orderId: number) => {
    const order = this.orderItemStore?.elements[orderId]
    if (!order?.data?.containerId) return

    await this.fetch([order.data.containerId], true)
  }

  fetchByOrderIds = async (orderIds: number[]) => {
    const orderIdsToFetch = orderIds.filter(
      orderId =>
        !this.journeysByInboundOrderId[orderId] && !this.journeysByOutboundOrderId[orderId],
    )
    if (!orderIdsToFetch.length) return

    const journeys = await containerJourneysService.getByOrderIds(orderIdsToFetch)

    runInAction(() => {
      this.upsertBulk(journeys)
    })
  }

  receiveContainerJourneysMessage = (event: IEvent<ContainerJourneyResponseDto[]>): void => {
    console.log('received container journeys', event.payload)
    if (event.payload) {
      this.upsertBulk(event.payload)

      this.updateLoadedContainerIds(event.payload.map(journey => journey.containerId))
    }
  }

  receiveContainerJourneysUpsertedEventMessage = (event: IEvent<ContainerJourneyResponseDto[]>) => {
    console.log('received ContainerJourneysUpsertedEvent', event.payload)
    if (event.payload) {
      this.upsertBulk(event.payload)
    }
  }

  private updateLoadedContainerIds(newLoadedContainerIds: number[]) {
    newLoadedContainerIds.forEach(id => this.loadedContainerIds.add(id))
  }
}
